/* ============================================================
   PP HEADER — enhancement on top of what already works with plain HTML/CSS.
   Principle: WITHOUT JS the menu still opens/closes (<details> + :hover).
   JS only handles the 5 things HTML can't do:
     1) publish the header's real height to --pp-header-h
     2) flag when the (CSS-only) pinned header has reached the top edge, so it can compact
     3) mega menu: click to close, ESC, hover with a delay
     4) drawer: lock background scroll, ESC, keep focus inside
     5) re-sync when the shop owner drags things around in the Theme Editor
   ============================================================ */
(function () {
  'use strict';

  var DESKTOP_MIN = 1000; // must match the breakpoint in pp-header.css
  var HOVER_CLOSE_MS = 160; // delay on mouse-leave, keeps the menu from flickering off when moving diagonally
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function isDesktop() {
    return window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)').matches;
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  /* ---------- 1. Publish the real height ----------
     The level-1 menu is allowed to wrap, so the height is NOT fixed.
     Other pages read --pp-header-h instead of guessing a hard-coded number. */
  function setupHeight(header) {
    function apply() {
      var h = Math.round(header.getBoundingClientRect().height);
      if (h > 0) {
        document.documentElement.style.setProperty('--pp-header-h', h + 'px');
      }
    }

    apply();

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(apply).observe(header);
    } else {
      window.addEventListener('resize', apply, { passive: true });
    }

    // When fonts finish loading the text changes height — measure again
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(apply).catch(function () {});
    }
  }

  /* ---------- 1b. Compact the header once it is pinned ----------
     The pinning itself is pure CSS (see the #header-group / .pp-hdr-shell block in pp-header.css);
     this only reports WHEN the sticky box has reached the top edge, so the CSS can shrink it.

     Why an IntersectionObserver and not a scroll listener: the scroller is not the same element on
     every screen — Horizon makes .page-wrapper the scroll container from 990px up and leaves the
     viewport as the scroller below that — so a scroll listener would have to know which one to
     bind to and rebind on resize. IntersectionObserver against the viewport is correct for both.

     rootMargin trims 1px off the top of the root so a box parked at top:0 counts as NOT fully
     visible; that is the crossing we want. Horizon achieves the same by parking its header at
     top:-1px, but that clips a visible pixel of the bar, so the margin is moved to the observer
     instead. The decision is read back off the measured rect, never off isIntersecting alone, so
     it stays correct no matter what fired the callback. */
  function setupStick(header) {
    var shell = header.closest('.pp-hdr-shell');
    if (!shell || typeof IntersectionObserver !== 'function') return;
    // The Theme Editor rebuilds the section's contents but keeps this wrapper, so without a flag
    // a re-init would stack a second observer on the same element.
    if (shell.dataset.ppStickWatched === '1') return;
    shell.dataset.ppStickWatched = '1';

    new IntersectionObserver(
      function (entries) {
        var entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.boundingClientRect.top <= 0) shell.setAttribute('data-pp-stuck', '');
        else shell.removeAttribute('data-pp-stuck');
      },
      { threshold: 1, rootMargin: '-1px 0px 0px 0px' }
    ).observe(shell);
  }

  /* ---------- 2. Mega menu ---------- */
  function setupMega(header) {
    var items = Array.prototype.slice.call(header.querySelectorAll('[data-pp-mega-item]'));
    if (!items.length) return;

    var current = null;
    var closeTimer = null;

    function panelOf(item) { return item.querySelector('[data-pp-mega]'); }
    function btnOf(item) { return item.querySelector('[data-pp-mega-btn]'); }

    function closeItem(item) {
      if (!item) return;
      var panel = panelOf(item);
      var btn = btnOf(item);
      if (panel) panel.classList.remove('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (current === item) current = null;
    }

    function closeAll() {
      items.forEach(closeItem);
      current = null;
    }

    function openItem(item) {
      if (!isDesktop()) return;
      if (current && current !== item) closeItem(current);
      var panel = panelOf(item);
      var btn = btnOf(item);
      if (panel) panel.classList.add('is-open');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      current = item;
    }

    function cancelClose() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function scheduleClose(item) {
      cancelClose();
      closeTimer = setTimeout(function () {
        closeItem(item);
      }, HOVER_CLOSE_MS);
    }

    items.forEach(function (item) {
      var btn = btnOf(item);
      if (!btn) return;

      btn.addEventListener('click', function () {
        cancelClose();
        if (current === item) closeItem(item);
        else openItem(item);
      });

      /* pointerenter with a mouse-only guard, not mouseenter: a finger tap fires the synthetic
         mouse sequence (mouseenter, then click) inside ONE gesture, so hover-open plus the click
         toggle above turned every tap into open-then-immediately-close on touch screens wide
         enough for the mega menu (iPad landscape, touch laptops — phones use the drawer).
         With the guard, a tap only runs the click handler, which toggles; closing by tapping
         anywhere outside still works through the document click listener below.
         Same guard, same reason as assets/pp-card-hover.js. */
      item.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        cancelClose();
        openItem(item);
      });

      item.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        scheduleClose(item);
      });

      // When focus leaves the item entirely (Tab to another item) close it, don't leave the panel hanging
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) closeItem(item);
      });
    });

    // ESC closes it and RETURNS focus to the exact button that opened it.
    // Listen on document, not on header: the menu can be opened by HOVER, and then focus
    // is still outside the header, so listening on the header would make ESC do nothing.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !current) return;
      var item = current;
      var btn = btnOf(item);
      // Only pull focus back when it's inside this item — if the user is typing in
      // the search box and presses ESC to clear it, yanking focus away would hijack their action.
      var focusWasInside = item.contains(document.activeElement);
      closeItem(item);
      if (focusWasInside && btn) btn.focus();
    });

    document.addEventListener('click', function (event) {
      if (current && !header.contains(event.target)) closeAll();
    });

    // Shrinking down to phone size, the mega no longer makes any sense
    window.addEventListener('resize', function () {
      if (!isDesktop()) closeAll();
    }, { passive: true });
  }

  /* ---------- 3. Mobile drawer ---------- */
  function lockScroll(on) {
    document.documentElement.classList.toggle('pp-hdr-lock', !!on);
  }

  function setupDrawer(header) {
    var drawer = header.querySelector('[data-pp-drawer]');
    if (!drawer) return;

    var toggle = drawer.querySelector('[data-pp-drawer-toggle]');
    var panel = drawer.querySelector('[data-pp-drawer-panel]');
    var isolatedElements = [];

    function isolateBackground(on) {
      if (!on) {
        isolatedElements.forEach(function (element) {
          element.removeAttribute('inert');
        });
        isolatedElements = [];
        return;
      }

      if (!panel || isolatedElements.length) return;

      /* Starting at the modal panel, inert every sibling branch up to <body>. The panel and its
         ancestors stay active; the header, page, cart drawer and other background UI leave both
         keyboard navigation and the accessibility tree until the menu closes. */
      var branch = panel;
      while (branch && branch !== document.body) {
        var parent = branch.parentElement;
        if (!parent) break;

        Array.prototype.forEach.call(parent.children, function (sibling) {
          if (sibling === branch || sibling === toggle || sibling.hasAttribute('inert')) return;
          sibling.setAttribute('inert', '');
          isolatedElements.push(sibling);
        });

        branch = parent;
      }
    }

    function close() {
      if (!drawer.open) return;
      drawer.open = false;
      isolateBackground(false);
      if (toggle) toggle.focus(); // return focus to the ☰ button
    }

    drawer.addEventListener('toggle', function () {
      lockScroll(drawer.open);
      isolateBackground(drawer.open);

      if (drawer.open && panel) {
        requestAnimationFrame(function () {
          var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), isVisible);
          if (nodes[0]) nodes[0].focus();
        });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!drawer.open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      // Keep focus inside the drawer: the background behind is covered, tabbing out would get lost
      var nodes = Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), isVisible);
      if (!nodes.length) return;

      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Rotating to landscape/desktop size: the drawer is hidden by CSS, so release the scroll lock
    window.addEventListener('resize', function () {
      if (isDesktop() && drawer.open) {
        drawer.open = false;
        isolateBackground(false);
        lockScroll(false);
      }
    }, { passive: true });
  }

  /* ---------- 4. Initialization ---------- */
  function init(header) {
    if (!header || header.dataset.ppHeaderReady === '1') return;
    header.dataset.ppHeaderReady = '1';
    // This flag turns off the pure-CSS :hover rule — with JS present, JS takes over to avoid click-to-close getting stuck
    header.classList.add('pp-hdr--js');
    setupHeight(header);
    setupStick(header);
    setupMega(header);
    setupDrawer(header);
  }

  function initAll(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-pp-header]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme Editor rebuilds the section — re-attach listeners and release any leftover scroll lock
  document.addEventListener('shopify:section:load', function (event) {
    lockScroll(false);
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    lockScroll(false);
  });
})();
